import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CallState } from '@lcs/shared/api';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { NavigatableDetail } from 'src/app/common-components/detail/detail';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import {
   getCallDetailsPrivileges,
   getDepartmentPrivileges,
   getExtensionPrivileges,
   getPhonePrivileges,
   getPromptPrivileges,
} from 'src/app/privileges/privileges.selector';
import { CallDetailsPrivileges } from '../../call-logs-register/models/call-details-privileges';
import { DepartmentPrivileges } from '../../department-register/models/department-privileges';
import { extensionRegisterAdapterActions } from '../../extension-register/state/extension-register.actions';
import { selectIds, selectTotalCount } from '../../extension-register/state/extension-register.reducer';
import { clearMailbox, resetMailbox } from '../../mailbox-messages/state/mailbox-message-detail.actions';
import { PhonePrivileges } from '../../phones/models/phone-privileges';
import { PromptPrivileges } from '../../prompt-register/models/prompt-privileges';
import { ExtensionHackedFraudDetails } from '../helpers/hacked-fraud-helper';
import { AddExistingPromptEvent } from '../models/add-existing-prompt-event.model';
import { AddNewPromptEvent } from '../models/add-new-prompt-event.model';
import { ExtensionDetailSaveEvent } from '../models/extension-detail-form-submit-event';
import {
   ExtensionDetailView,
   ExtensionNumberDetail,
   ExternalNumberDetail,
} from '../models/extension-detail-view.model';
import { ExtensionPrivileges } from '../models/extension-privileges';
import { ExtensionDetailFormService } from '../services/extension-detail-form.service';
import * as ExtensionDetailActions from '../state/extension-detail.actions';
import {
   getExtension,
   getExtensionCallState,
   getExtensionNumbers,
   getExternalNumbers,
   getHackedFraudDetails,
} from '../state/extension-detail.selectors';

@Component({
    encapsulation: ViewEncapsulation.None,
    host: { class: 'entity-detail' },
    providers: [ExtensionDetailFormService, { provide: FormsBase, useExisting: ExtensionDetailFormService }],
    selector: 'ndt-extension-detail',
    templateUrl: './extension-detail.component.html',
    standalone: false
})
export class ExtensionDetailComponent
   extends NavigatableDetail<ExtensionDetailFormService>
   implements OnInit, OnDestroy
{
   extension$: Observable<ExtensionDetailView | null>;

   hackedFraudDetails$: Observable<ExtensionHackedFraudDetails | null>;

   callState$: Observable<CallState>;

   extensionPrivileges$: Observable<ExtensionPrivileges>;

   promptPrivileges$: Observable<PromptPrivileges>;

   phonePrivileges$: Observable<PhonePrivileges>;

   callDetailsPrivileges$: Observable<CallDetailsPrivileges>;

   departmentPrivileges$: Observable<DepartmentPrivileges>;

   destroyed$ = new Subject<void>();

   extensionNumbers$: Observable<ExtensionNumberDetail[]>;

   externalNumbers$: Observable<ExternalNumberDetail[]>;

   constructor(public store: Store, private router: Router, protected formService: ExtensionDetailFormService) {
      super(store, formService);
   }

   ngOnInit(): void {
      this.extension$ = this.store.pipe(select(getExtension));
      this.callState$ = this.store.pipe(select(getExtensionCallState));
      this.externalNumbers$ = this.store.pipe(select(getExternalNumbers));
      this.hackedFraudDetails$ = this.store.pipe(select(getHackedFraudDetails));
      this.extensionPrivileges$ = this.store.pipe(select(getExtensionPrivileges));
      this.phonePrivileges$ = this.store.pipe(select(getPhonePrivileges));
      this.promptPrivileges$ = this.store.pipe(select(getPromptPrivileges));
      this.callDetailsPrivileges$ = this.store.pipe(select(getCallDetailsPrivileges));
      this.departmentPrivileges$ = this.store.pipe(select(getDepartmentPrivileges));
      this.extensionNumbers$ = this.store.pipe(select(getExtensionNumbers));
      this.store.dispatch(ExtensionDetailActions.extensionDetailLoad());
      this.router.events.pipe(takeUntil(this.destroyed$)).subscribe((e) => {
         if (e instanceof NavigationEnd) {
            this.store.dispatch(ExtensionDetailActions.extensionDetailLoad());
         }
      });
      this.totalCount$ = this.store.pipe(select(selectTotalCount));
      this.idsFromRegister$ = this.store.pipe(select(selectIds));
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onFormSaveCanceled(): void {
      this.store.dispatch(ExtensionDetailActions.extensionDetailCancel());
   }

   onFormSubmitted(event: ExtensionDetailSaveEvent) {
      this.store.dispatch(ExtensionDetailActions.saveExtensionDetail(event));
   }

   onDeleteExtension(event: { extensionId: number; number: number }) {
      this.store.dispatch(ExtensionDetailActions.deleteExtension(event));
   }

   onAddExistingPrompt(event: AddExistingPromptEvent): void {
      this.store.dispatch(ExtensionDetailActions.addExistingPrompt(event));
   }

   onAddNewPrompt(event: AddNewPromptEvent): void {
      this.store.dispatch(
         ExtensionDetailActions.addNewPrompt({
            promptType: event.promptType,
            voiceMailGreetingPrompt: event.voiceMailGreetingPrompt,
         })
      );
   }

   onAddNewTimeBasedRoute(): void {
      this.store.dispatch(ExtensionDetailActions.addNewTimeBasedRoute());
   }

   onAddNewFindMeFollowMeStep(): void {
      this.store.dispatch(ExtensionDetailActions.addNewFindMeFollowMeStep());
   }

   onEditMailboxPin(event): void {
      this.store.dispatch(ExtensionDetailActions.editMailboxPin());
   }

   onNavigate(id: string | number) {
      this.extensionNumbers$.pipe(take(1)).subscribe((extensionNumbers) => {
         this.store.dispatch(ExtensionDetailActions.navigate({ id, extensionNumbersWithTypes: extensionNumbers }));
      });
   }

   onLoadIdPage() {
      this.store.dispatch(extensionRegisterAdapterActions.loadIdPage());
   }

   onResetHackedAndFraud(hackedFraudDetails: ExtensionHackedFraudDetails): void {
      const hackedAndFraudSipUserIds = hackedFraudDetails.hackedAndFraudSipUserIds;

      if (
         hackedAndFraudSipUserIds !== null &&
         hackedAndFraudSipUserIds !== undefined &&
         hackedAndFraudSipUserIds.length > 0
      ) {
         this.store.dispatch(ExtensionDetailActions.changeSipUserPassword({ sipUserIds: hackedAndFraudSipUserIds }));
      }
   }

   onResetMwi(event): void {
      this.store.dispatch(ExtensionDetailActions.resetMwi({ extensionId: event, accountId: undefined }));
   }

   onChangeDNDStatus(dndStatus: string | null) {
      this.store.dispatch(ExtensionDetailActions.changeDNDStatus({ dndStatus }));
   }

   onClearMailbox(mailboxMessageIds: number[]) {
      this.store.dispatch(
         clearMailbox({
            mailboxMessageIds,
         })
      );
   }

   onResetMailbox(data: { mailboxId: number; mailboxMessageIds: number[] }) {
      this.store.dispatch(
         resetMailbox({
            mailboxId: data.mailboxId,
            mailboxMessageIds: data.mailboxMessageIds,
         })
      );
   }
}
